import type { PutRatePolicyRequest, StartJobRequest } from '@zyx/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApi } from '@/hooks/use-connection';

const JOBS = ['jobs'] as const;
const RATES = ['rate-policies'] as const;

/** How often the live views ask again. Rates move fast; jobs do not. */
const JOBS_POLL_MS = 4_000;
const RATES_POLL_MS = 2_000;

export function useJobs() {
  const api = useApi();
  return useQuery({ queryKey: [...JOBS, 'list'], queryFn: () => api.listJobs({ limit: 50 }), refetchInterval: JOBS_POLL_MS });
}

export function useDeadItems(jobId: number, enabled: boolean) {
  const api = useApi();
  return useQuery({ queryKey: [...JOBS, 'dead', jobId], queryFn: () => api.listDeadItems({ jobId, limit: 100 }), enabled, refetchInterval: JOBS_POLL_MS });
}

function failed(error: unknown) {
  toast.error(error instanceof Error ? error.message : 'The request failed.');
}

export function useStartJob() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: StartJobRequest) => api.startJob(request),
    onSuccess: async ({ job }) => {
      toast.success(`Started job ${job.jobId}.`);
      await client.invalidateQueries({ queryKey: JOBS });
    },
    onError: failed,
  });
}

export type JobAction = 'pause' | 'resume' | 'cancel' | 'retry';

export function useJobAction() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, action }: { readonly jobId: number; readonly action: JobAction }) => {
      if (action === 'retry') {
        const { retried } = await api.retryDeadItems(jobId);
        toast.success(retried === 0 ? 'No dead items to retry.' : `${retried} item(s) back in the queue.`);
        return;
      }
      await (action === 'pause' ? api.pauseJob(jobId) : action === 'resume' ? api.resumeJob(jobId) : api.cancelJob(jobId));
    },
    onSuccess: () => client.invalidateQueries({ queryKey: JOBS }),
    onError: failed,
  });
}

/** The policies and their live stats; the service keeps each one's last minute for the sparkline. */
export function useRatePolicies() {
  const api = useApi();
  return useQuery({ queryKey: RATES, queryFn: () => api.listRatePolicies(), refetchInterval: RATES_POLL_MS });
}

export function usePutRatePolicy() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: PutRatePolicyRequest) => api.putRatePolicy(request),
    onSuccess: async ({ policy }) => {
      toast.success(`Saved ${policy.policyId}. It applies to the next request.`);
      await client.invalidateQueries({ queryKey: RATES });
    },
    onError: failed,
  });
}

export function useDeleteRatePolicy() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (policyId: string) => api.deleteRatePolicy(policyId),
    onSuccess: () => client.invalidateQueries({ queryKey: RATES }),
    onError: failed,
  });
}
